/**
 * SplitButton — primary action + caret menu in one pill.
 *
 * Design system mapping (mch-button-split):
 *   • Wrapper: rounded-full overflow-hidden bg matching variant
 *   • Primary: padding inherits from Button
 *   • Caret:   border-l white/25 padding 0 10px
 *
 * Composition:
 *   <SplitButton
 *     variant="primary"
 *     onClick={addWorkspace}
 *     menu={
 *       <>
 *         <DropdownMenu.Item onClick={...}>Add room</DropdownMenu.Item>
 *         <DropdownMenu.Item onClick={...}>Bulk import</DropdownMenu.Item>
 *       </>
 *     }
 *   >
 *     Add workspace
 *   </SplitButton>
 */
import * as React from "react"
import { Button as BaseButton } from "@base-ui-components/react/button"
import { cn } from "@lib/cn"
import { buttonVariants, type ButtonProps } from "./Button"
import { DropdownMenu } from "./DropdownMenu"

export interface SplitButtonProps
  extends Omit<ButtonProps, "render" | "leadingIcon" | "trailingIcon" | "loading"> {
  menu: React.ReactNode
  caretAriaLabel?: string
}

export function SplitButton({
  className,
  variant = "primary",
  size = "default",
  disabled,
  menu,
  caretAriaLabel = "More actions",
  children,
  onClick,
  ...props
}: SplitButtonProps) {
  const wrapperBase = "inline-flex items-stretch rounded-full overflow-hidden"
  const splitBg = buttonVariants({ variant, size, fullWidth: false })

  return (
    <span className={cn(wrapperBase, className)}>
      <BaseButton
        className={cn(
          splitBg,
          "rounded-none rounded-l-full focus-visible:z-10",
          "border-r border-white/25",
        )}
        disabled={disabled}
        onClick={onClick}
        {...props}
      >
        {children}
      </BaseButton>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger
          disabled={disabled}
          aria-label={caretAriaLabel}
          className={cn(
            splitBg,
            "rounded-none rounded-r-full px-2 focus-visible:z-10",
          )}
        >
          <svg viewBox="0 0 12 12" className="size-3" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <polyline points="3,4.5 6,8 9,4.5" />
          </svg>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content>{menu}</DropdownMenu.Content>
      </DropdownMenu.Root>
    </span>
  )
}
